// NOTIF-01：頂列鈴鐺（未讀數）+ 收件匣面板 + 浮動通知（toast）。
// 來源是 notify 的收件匣（每個 plane 一份，lib/notifications.ts），所以使用者在 notify 設的偏好 / 勿擾 / 去重都已套用——
// VMS 不自己從事件判斷要不要通知。「即時」：事件串流（/v1/vms/stream）每來一筆就 1.5 秒後重抓收件匣（notify 送達要一點時間），
// 平常每 30 秒輪詢一次。
// 浮動通知（SPEC UX-14，VMS UX 評審 2026-10-13 P0-1、B-01）：
//   ① 只有警報類會跳（個人三檔：只有警報／警報與偵測／都不跳，存這台電腦）
//   ② 右下角、同時 1 張，其餘收成「還有 N 則通知 ▸」（點了打開收件匣）；低於對話框（對話框開著時不顯示、關掉後再補）；滑鼠移上去暫停 8 秒倒數
//   ③ 卡片本身不換頁；〔查看〕才換頁並標已讀，〔✕〕只關掉、不標已讀。收件匣面板同理：點一則先展開預覽，〔開啟〕才換頁
//   ④ 標題「警報 · <完整設備名>」，內文「<事件顯示名> · <時間>」
//   ⑤ 有設備的警報類不跳這裡的浮動通知，一律交給警報卡（features/events/SensorPopups，同一套樣式與收合；第四輪 R4-P0-1、B4-01 / B4-02）；
//     跳過的 id 與時間記在 localStorage，重新載入、換頁不重跳；頁面載入前的通知一律算舊的
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { useLiveEvents } from '../../lib/liveEvents';
import {
  freshForToast,
  inScope,
  inboxDeviceName,
  inboxTitle,
  inboxTarget,
  markInbox,
  VARIANT_DELEGATOR_MFA,
  mergeInbox,
  planeInbox,
  savedToastLevel,
  toastKind,
  unreadCount,
  type InboxItem,
} from '../../lib/notifications';
import { shortStamp, sinceStamp } from '../../lib/timefmt';
import { useAlarmStates } from '../../lib/alarmStates';
import { loadInboxSeen, offerInboxAlarm, popupActive, poppedRecently, saveInboxSeen, subscribePopupActive, toastSince } from '../../lib/alarmPopup';
import { useDismiss } from '../../components/useDismiss';
import { groupAlarms } from '../../lib/alarmGroup';
import { EventWhat } from '../../lib/useEventWhat';

const POLL_MS = 30_000;
const TOAST_MS = 8_000;
/** 排隊等著跳的最多幾則（其餘只在收件匣）。 */
const QUEUE_MAX = 20;
/** 這一頁載入的時間往前 1 分鐘：比這更早的通知一律算舊的（不靠收件匣第一次讀到什麼；容許一點時鐘差） */
const PAGE_LOADED_AT = new Date(Date.now() - 60_000).toISOString();

/** 對話框（原生 <dialog> 或 aria-modal）開著嗎：開著時浮動通知不顯示（UX-14 ②）。 */
export function modalOpen(): boolean {
  return !!document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]');
}

export function useInbox() {
  const { planes, homes, visible } = useScope();
  const qc = useQueryClient();
  const qs = useQueries({
    queries: planes.map((p) => ({
      queryKey: ['inbox', p.api],
      queryFn: () => planeInbox(p),
      refetchInterval: POLL_MS,
      retry: false,
    })),
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const all = useMemo(() => mergeInbox(qs.map((q) => q.data)), [JSON.stringify(qs.map((q) => q.dataUpdatedAt))]);
  // 範圍：目前範圍看得到的家戶；沒有任何家戶（剛登入還在載入）時不篩
  const homeIds = useMemo(() => new Set((visible.length ? visible : homes).map((h) => h.home_id)), [visible, homes]);
  const items = useMemo(() => inScope(all, homeIds), [all, homeIds]);
  const refetch = () => void qc.invalidateQueries({ queryKey: ['inbox'] });
  // ready：plane 清單已知、每個 plane 的收件匣都讀過一次（成功或失敗）——浮動通知的起點要等這時才定，
  // 不然 plane 還沒載入時是空清單、起點變成 1970，之後整批舊通知都會跳（第四輪 B4-01 重新載入重跳）
  // 至少一個 plane 讀成功才算（剛登入 token 還沒好時第一次會失敗，失敗的空清單不能當起點）
  const ready = planes.length > 0 && qs.every((q) => q.isFetched) && qs.some((q) => q.isSuccess);
  return { items, all, homeIds, loading: qs.some((q) => q.isLoading), ready, failed: qs.length > 0 && qs.every((q) => q.isError), refetch };
}

type Toast = InboxItem & { left: number };

export function InboxBell({ toastTarget }: { toastTarget?: HTMLElement | null } = {}) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { unhandled: unhandledAlarms } = useAlarmStates();
  const { devices } = useScope();
  const { items, all: allItems, homeIds, loading, ready, failed, refetch } = useInbox();
  const { subscribe } = useLiveEvents();
  const [open, setOpen] = useState(false);
  // 鈴鐺面板跟其他選單一樣：Esc、點外面都關（第三輪 B3-17）
  const bellRef = useRef<HTMLSpanElement>(null);
  useDismiss(open, () => setOpen(false), bellRef);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [queue, setQueue] = useState<Toast[]>([]);
  const [hover, setHover] = useState(false);
  const [modal, setModal] = useState(false);
  /** 跳過的（浮動通知或交給警報卡的）nid：跨重新載入記住（localStorage），換頁、重新載入都不重跳（第四輪 B4-01） */
  const toasted = useRef<Set<string>>(new Set(loadInboxSeen().ids));
  /** 第一次載入的最新一筆、與上次記下的時間取較新的；之前的都不跳（登入、重新載入時不要一次跳一堆舊的，NOTIF-01） */
  const since = useRef<string | null>(null);
  const [readLocal, setReadLocal] = useState(new Set<string>());
  const shown = items.map((i) => (readLocal.has(i.nid) && !i.read_at ? { ...i, read_at: 'local' } : i));
  const unread = unreadCount(shown);
  // 同一台＋同一種、相鄰 2 分鐘內的合成一列「× N 次」，✓ 一次標整波（第四輪 R4-P1-2、B4-08）
  const groups = groupAlarms(
    shown,
    (i) => (i.home_id && i.device_ref ? `${i.home_id}/${i.device_ref}/${i.category}/${i.variant}/${i.body}` : null),
    (i) => Date.parse(i.created_at) || 0,
    // 還沒讀的同一台＋同一種一直併（第五輪 B5-10）
    { open: (i) => !i.read_at },
  );

  // 事件串流 → 稍後重抓（debounce）
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = subscribe(() => {
      clearTimeout(timer);
      timer = setTimeout(refetch, 1_500);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  useEffect(() => {
    if (loading || !ready) return;
    // 起點與「新到的」用全部家戶的收件匣：有設備的警報類不受目前範圍限制、照樣出警報卡（卡上有〔切到該範圍〕；第六輪 R6-P1-7，
    // SPEC UX-14 ⑥「不在目前範圍的設備照樣寫完整名」）；其他浮動通知仍只跳目前範圍的
    const alarmCard = (i: InboxItem) => toastKind(i.category) === 'alarm' && !!i.home_id && !!i.device_ref && i.variant !== VARIANT_DELEGATOR_MFA;
    if (since.current === null) {
      since.current = toastSince(allItems[0]?.created_at, loadInboxSeen().since, PAGE_LOADED_AT);
      saveInboxSeen(since.current, toasted.current);
      return;
    }
    // 同一台剛被警報卡帶出過的警報類通知不再跳（只進收件匣；第三輪 B3-02「只有一套」）
    const fresh = freshForToast(allItems, since.current, toasted.current, savedToastLevel()).filter(
      (i) => (alarmCard(i) || !i.home_id || homeIds.has(i.home_id)) && !(toastKind(i.category) === 'alarm' && i.home_id && i.device_ref && poppedRecently(`${i.home_id}/${i.device_ref}`)),
    );
    if (allItems[0] && (Date.parse(allItems[0].created_at) || 0) > (Date.parse(since.current) || 0)) since.current = allItems[0].created_at;
    fresh.forEach((i) => toasted.current.add(i.nid));
    saveInboxSeen(since.current, toasted.current);
    if (!fresh.length) return;
    // 有設備的警報類一律交給警報卡（同一種樣式、20 秒收合；第四輪 R4-P0-1、B4-01 / B4-02）；
    // 這個視窗沒有警報卡（多螢幕的非警報視窗）才照舊跳浮動通知
    // 舊的先交，最新的留在警報卡最上面
    const toToast = [...fresh].reverse().filter((i) => {
      if (toastKind(i.category) !== 'alarm' || !i.home_id || !i.device_ref || i.variant === VARIANT_DELEGATOR_MFA) return true;
      return !offerInboxAlarm({
        nid: i.nid,
        homeId: i.home_id,
        deviceRef: i.device_ref,
        eventId: i.event_id,
        name: inboxDeviceName(devices, i),
        what: inboxTitle(i.body || i.title, ''),
        at: Date.parse(i.created_at) || Date.now(),
      });
    });
    if (!toToast.length) return;
    setQueue((l) => [...toToast.reverse().map((i) => ({ ...i, left: TOAST_MS })), ...l].slice(0, QUEUE_MAX));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, loading, ready]);

  // 倒數只算最上面那張；滑鼠在上面或對話框開著時暫停（對話框開著時也不顯示）
  useEffect(() => {
    if (!queue.length) return;
    const iv = setInterval(() => {
      const m = modalOpen();
      setModal(m);
      if (m || hover || popupActive() > 0) return; // 警報卡展開時也暫停（排隊等它收掉）
      setQueue((l) => {
        if (!l.length) return l;
        const [head, ...rest] = l;
        const left = head.left - 500;
        return left <= 0 ? rest.map((x, i) => (i === 0 ? { ...x, left: TOAST_MS } : x)) : [{ ...head, left }, ...rest];
      });
    }, 500);
    return () => clearInterval(iv);
  }, [queue.length, hover]);

  const markRead = (i: InboxItem) => {
    if (i.read_at) return;
    setReadLocal((s) => new Set(s).add(i.nid));
    void markInbox(i.api, i.nid, 'read').then(refetch, () => undefined);
  };
  /** 〔查看〕〔開啟〕：標已讀 + opened，再換頁（只有這兩顆按鈕會換頁）。 */
  const openItem = (i: InboxItem) => {
    const to = inboxTarget(i);
    markRead(i);
    void markInbox(i.api, i.nid, 'opened').catch(() => undefined);
    setQueue((l) => l.filter((x) => x.nid !== i.nid));
    if (to) {
      setOpen(false);
      nav(to);
    }
  };
  const dismiss = (nid: string) => setQueue((l) => l.filter((x) => x.nid !== nid).map((x, i) => (i === 0 ? { ...x, left: TOAST_MS } : x)));
  // 同一天只寫時:分（第六輪 B6-04：「2026-10-14 16:07 · × 23 次（最早 2026-10-14 16:00）」同一天還重寫日期）
  const time = (s: string) => shortStamp(s);
  /** 標題：警報類寫「警報 · <完整設備名>」；找不到設備或不是警報類就用 notify 的標題。 */
  const titleOf = (i: InboxItem) => {
    const dev = inboxDeviceName(devices, i);
    if (dev && toastKind(i.category) === 'alarm') return t('inbox.alarm_title', { name: dev });
    return inboxTitle(i.title, dev) || t('inbox.untitled');
  };
  const bodyOf = (i: InboxItem) => [toastKind(i.category) === 'alarm' && inboxDeviceName(devices, i) ? i.body || i.title : i.body, time(i.created_at)].filter(Boolean).join(' · ');

  // 警報類的內文用事件本身的顯示名（notify 範本是粗分類：「設備被拆除或遮擋」其實是影像訊號中斷；第七輪 B7-01）
  const bodyText = (i: InboxItem) =>
    toastKind(i.category) === 'alarm' && i.home_id && i.event_id ? <EventWhat homeId={i.home_id} eventId={i.event_id} fallback={i.body} /> : i.body;
  // 警報卡展開在右下時不跳（同一個位置同時只有一張；等卡片收掉再補）
  const popupOpen = useSyncExternalStore(subscribePopupActive, popupActive, popupActive) > 0;
  const head = queue[0];
  const toastList = head && !modal && !popupOpen && (
    <div className="inbox-toasts" role="status" aria-live="polite" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="inbox-toast card" data-nid={head.nid} data-kind={toastKind(head.category) ?? ''}>
        <div className="inbox-toast-text">
          <span className="inbox-title">{titleOf(head)}</span>
          <span className="inbox-body">{bodyOf(head)}</span>
        </div>
        <div className="inbox-toast-actions">
          {inboxTarget(head) && (
            <button className="btn btn-sm" onClick={() => openItem(head)} data-testid="toast-view">
              {t('inbox.view')}
            </button>
          )}
          <button className="icon-btn" aria-label={t('common.close')} title={t('inbox.dismiss_tip')} onClick={() => dismiss(head.nid)}>
            ✕
          </button>
        </div>
      </div>
      {queue.length > 1 && (
        <button className="inbox-more" onClick={() => setOpen(true)} data-testid="toast-more">
          {t('inbox.more_n', { count: queue.length - 1 })} ▸
        </button>
      )}
    </div>
  );

  return (
    <span className="inbox-wrap" ref={bellRef}>
      <button
        className="btn inbox-bell"
        aria-label={t('inbox.title_n', { n: unread })}
        title={t('inbox.bell_tip', { n: unread })}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        🔔
        {unread > 0 && (
          <span className="inbox-badge" data-testid="inbox-unread">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="inbox-panel card" role="dialog" aria-label={t('inbox.title')}>
          <div className="toolbar">
            <strong>{t('inbox.title')}</strong>
            <span className="spacer" />
            {unread > 0 && (
              <button className="btn btn-sm" onClick={() => shown.filter((i) => !i.read_at).forEach(markRead)}>
                {t('inbox.mark_all')}
              </button>
            )}
            <button className="icon-btn" aria-label={t('common.close')} onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
          {failed && <p className="muted small">{t('inbox.failed')}</p>}
          {/* 沒有通知、但還有沒處理的警報：指去警報分頁（第六輪 B6-04：lstw 寫「沒有通知」，同時有 935 則未處理） */}
          {!failed && !loading && shown.length === 0 && (
            <p className="muted" data-testid="inbox-empty">
              {unhandledAlarms ? t('inbox.empty_alarms', { count: unhandledAlarms }) : t('inbox.empty')}{' '}
              {!!unhandledAlarms && (
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    setOpen(false);
                    nav('/events?tab=alarms');
                  }}
                  data-testid="inbox-go-alarms"
                >
                  {t('inbox.go_alarms')}
                </button>
              )}
            </p>
          )}
          <ul className="inbox-list">
            {groups.map(({ head: i, rest }) => {
              const ex = expanded === i.nid;
              const to = inboxTarget(i);
              const all = [i, ...rest];
              const unreadIn = all.filter((x) => !x.read_at);
              return (
                <li key={i.nid} className={(unreadIn.length ? 'unread' : 'read') + (ex ? ' expanded' : '')} data-nid={i.nid} data-category={i.category} data-group-n={rest.length ? all.length : undefined}>
                  <div className="inbox-entry">
                    <button className="inbox-item" aria-expanded={ex} onClick={() => setExpanded(ex ? null : i.nid)}>
                      <span className="inbox-title">{titleOf(i)}</span>
                      {!ex && i.body && <span className="inbox-body">{bodyText(i)}</span>}
                      <span className="muted small">
                        {time(i.created_at)}
                        {rest.length > 0 && (
                          <span data-testid="inbox-times">
                            {' · '}
                            {/* 最早跟最新同一分鐘就不再寫一次時間（B5-10「13:47 · × 2 次（最早 13:47）」） */}
                            {!sinceStamp(rest[rest.length - 1].created_at, i.created_at)
                              ? t('inbox.times_only', { n: all.length })
                              : t('inbox.times_n', { n: all.length, since: sinceStamp(rest[rest.length - 1].created_at, i.created_at) })}
                          </span>
                        )}
                      </span>
                    </button>
                    {ex && (
                      <div className="inbox-preview">
                        {inboxDeviceName(devices, i) && <div className="small">{inboxDeviceName(devices, i)}</div>}
                        {i.body && <div className="inbox-body">{bodyText(i)}</div>}
                        {to && (
                          <button className="btn btn-sm btn-primary" onClick={() => openItem(i)}>
                            {t('inbox.open')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {unreadIn.length > 0 && (
                    <button className="icon-btn inbox-mark" title={t('inbox.mark_read')} aria-label={t('inbox.mark_read')} onClick={() => unreadIn.forEach(markRead)}>
                      ✓
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {toastList && (toastTarget ? createPortal(toastList, toastTarget) : toastList)}
    </span>
  );
}
